import React from 'react'
import { Box, Card, Typography } from '@mui/material'
import {
  Build,
  CompareArrows,
  Email,
  Group,
  Layers,
  LockOpen,
  Notifications,
  Schedule,
  Search,
  Sync,
  VpnKey,
  Warning,
} from '@mui/icons-material'
import AppLink from '../util/AppLink'
import { serif } from './brand'
import { IconTile, Page, useBrand } from './ui'

type Tool = {
  to: string
  title: string
  text: string
  icon: React.ReactNode
}

const groups: { title: string; tools: Tool[] }[] = [
  {
    title: 'eRecords',
    tools: [
      {
        to: '/admin/teams',
        title: 'Microsoft Teams',
        text: 'Posting flow, test cards, which channels alerts go to.',
        icon: <Notifications />,
      },
      {
        to: '/admin/entra-sync',
        title: 'Microsoft Entra sync',
        text: 'Users, phones, photos and admin roles from Entra. Sync now.',
        icon: <Sync />,
      },
    ],
  },
  {
    title: 'Monitoring',
    tools: [
      {
        to: '/admin/message-logs',
        title: 'Message logs',
        text: 'Every call, SMS and Teams message, with delivery status.',
        icon: <Email />,
      },
      {
        to: '/admin/alert-counts',
        title: 'Alert counts',
        text: 'How many alerts each service raised over time.',
        icon: <Warning />,
      },
      {
        to: '/admin/service-metrics',
        title: 'Service metrics',
        text: 'Time to acknowledge and close, per service.',
        icon: <Layers />,
      },
    ],
  },
  {
    title: 'Settings',
    tools: [
      {
        to: '/admin/config',
        title: 'Configuration',
        text: 'Sign-in providers, Twilio, webhooks and other settings.',
        icon: <Build />,
      },
      {
        to: '/admin/api-keys',
        title: 'API keys',
        text: 'Keys for scripts and integrations that call the API.',
        icon: <VpnKey />,
      },
      {
        to: '/admin/limits',
        title: 'System limits',
        text: 'Caps on rules, steps, contact methods and more.',
        icon: <LockOpen />,
      },
    ],
  },
  {
    title: 'Maintenance',
    tools: [
      {
        to: '/admin/maintenance',
        title: 'Maintenance mode',
        text: 'Pause all notifications for planned work.',
        icon: <Schedule />,
      },
      {
        to: '/admin/toolbox',
        title: 'Toolbox',
        text: 'Send a test SMS or look up a phone number.',
        icon: <Search />,
      },
      {
        to: '/admin/switchover',
        title: 'Database switchover',
        text: 'Move to a new database with no downtime.',
        icon: <CompareArrows />,
      },
    ],
  },
]

// AdminPanel replaces the Admin submenu with one page of tiles.
export default function AdminPanel(): React.JSX.Element {
  const c = useBrand()
  return (
    <Page title='Admin' subtitle='Settings and tools for administrators.'>
      {groups.map((g) => (
        <Box key={g.title} sx={{ mb: 3 }}>
          <Typography
            sx={{ fontFamily: serif, fontSize: 18, color: c.ink, mb: 1.25 }}
          >
            {g.title}
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gap: 1.5,
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, minmax(0, 1fr))',
                lg: 'repeat(3, minmax(0, 1fr))',
              },
            }}
          >
            {g.tools.map((t) => (
              <Card
                key={t.to}
                component={AppLink}
                to={t.to}
                underline='none'
                sx={{
                  p: 2,
                  display: 'flex',
                  gap: 1.5,
                  alignItems: 'flex-start',
                  color: c.ink,
                  transition: 'border-color .15s, box-shadow .15s',
                  '&:hover': {
                    borderColor: c.muted,
                    boxShadow: '0 8px 24px rgba(51,49,50,0.08)',
                  },
                }}
              >
                <IconTile>{t.icon}</IconTile>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 600 }}>{t.title}</Typography>
                  <Typography sx={{ fontSize: 13, color: c.muted }}>
                    {t.text}
                  </Typography>
                </Box>
              </Card>
            ))}
          </Box>
        </Box>
      ))}
      <Typography sx={{ fontSize: 13, color: c.muted }}>
        <Group sx={{ fontSize: 16, verticalAlign: 'text-bottom', mr: 0.5 }} />
        Users are managed in Microsoft Entra; see Users for their phones and
        roles.
      </Typography>
    </Page>
  )
}
